import { toast } from "sonner";

const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'lovable-project-z9kvs.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
// Note: Shopify Storefront tokens are designed for public client-side use per Shopify architecture
// This token only grants read access to public product data and cannot modify store settings
const SHOPIFY_STOREFRONT_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN || '21e37ed2df13d849f4aa1fb94c243e84';

// Metafield interface for translations
export interface MetafieldValue {
  value: string | null;
}

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
    // Translation metafields
    title_en?: MetafieldValue | null;
    title_ru?: MetafieldValue | null;
    desc_en?: MetafieldValue | null;
    desc_ru?: MetafieldValue | null;
  };
}

const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String, $after: String) {
    products(first: $first, query: $query, after: $after, sortKey: CREATED_AT, reverse: true) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          description
          handle
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
          title_en: metafield(namespace: "translations", key: "title_en") { value }
          title_ru: metafield(namespace: "translations", key: "title_ru") { value }
          desc_en: metafield(namespace: "translations", key: "desc_en") { value }
          desc_ru: metafield(namespace: "translations", key: "desc_ru") { value }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 20) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
      title_en: metafield(namespace: "translations", key: "title_en") { value }
      title_ru: metafield(namespace: "translations", key: "title_ru") { value }
      desc_en: metafield(namespace: "translations", key: "desc_en") { value }
      desc_ru: metafield(namespace: "translations", key: "desc_ru") { value }
    }
  }
`;


// Request cache for deduplication
const requestCache = new Map<string, { promise: Promise<unknown>; timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  // Create cache key from query + variables
  const cacheKey = JSON.stringify({ query, variables });
  const now = Date.now();
  
  // Check cache for recent identical requests
  const cached = requestCache.get(cacheKey);
  if (cached && now - cached.timestamp < CACHE_TTL) {
    return cached.promise;
  }
  
  const fetchPromise = (async () => {
    const response = await fetch(SHOPIFY_STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (response.status === 402) {
      toast.error("Shopify: Payment required", {
        description: "Shopify API access requires an active Shopify billing plan."
      });
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
    }

    return data;
  })();
  
  // Store in cache
  requestCache.set(cacheKey, { promise: fetchPromise, timestamp: now });
  
  // Clean old cache entries periodically
  if (requestCache.size > 50) {
    for (const [key, value] of requestCache.entries()) {
      if (now - value.timestamp > CACHE_TTL) {
        requestCache.delete(key);
      }
    }
  }
  
  return fetchPromise;
}

export interface ProductsResponse {
  products: ShopifyProduct[];
  hasNextPage: boolean;
  endCursor: string | null;
}

export async function fetchProducts(first: number = 20, query?: string, after?: string): Promise<ProductsResponse> {
  const data = await storefrontApiRequest(STOREFRONT_QUERY, { first, query, after });
  if (!data) return { products: [], hasNextPage: false, endCursor: null };
  
  const productsData = data.data.products;
  return {
    products: productsData.edges || [],
    hasNextPage: productsData.pageInfo?.hasNextPage || false,
    endCursor: productsData.pageInfo?.endCursor || null,
  };
}

export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
  if (!data) return null;
  return data.data.productByHandle;
}

// Cart mutations
const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export interface CartItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

export async function createStorefrontCheckout(items: CartItem[]): Promise<string> {
  console.log('[SHOPIFY] Creating checkout for items:', items.length);
  
  const lines = items.map(item => ({
    quantity: item.quantity,
    merchandiseId: item.variantId,
  }));

  console.log('[SHOPIFY] Cart lines:', lines);

  // Don't use cache for cart creation - each checkout must be unique
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query: CART_CREATE_MUTATION,
      variables: { input: { lines } },
    }),
  });

  if (response.status === 402) {
    console.error('[SHOPIFY] Payment required error');
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active Shopify billing plan."
    });
    throw new Error('Payment required');
  }

  if (!response.ok) {
    console.error('[SHOPIFY] HTTP error:', response.status);
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const cartData = await response.json();
  console.log('[SHOPIFY] Cart response:', cartData);

  if (cartData.errors) {
    console.error('[SHOPIFY] GraphQL errors:', cartData.errors);
    throw new Error(`Error calling Shopify: ${cartData.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  if (cartData.data.cartCreate.userErrors.length > 0) {
    console.error('[SHOPIFY] User errors:', cartData.data.cartCreate.userErrors);
    throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  const cart = cartData.data.cartCreate.cart;
  
  if (!cart?.checkoutUrl) {
    console.error('[SHOPIFY] No checkout URL in response');
    throw new Error('No checkout URL returned from Shopify');
  }

  // Force myshopify.com domain for checkout to avoid custom domain issues
  const url = new URL(cart.checkoutUrl);
  // Replace any custom domain with the myshopify.com domain
  url.hostname = SHOPIFY_STORE_PERMANENT_DOMAIN;
  url.searchParams.set('channel', 'online_store');
  
  console.log('[SHOPIFY] Final checkout URL:', url.toString());
  return url.toString();
}
